import React, { useState, useRef, useEffect, useMemo } from 'react'
import { HiMagnifyingGlass } from 'react-icons/hi2'
import { FaRegBuilding } from 'react-icons/fa'
import { cn, processRichTextForDisplay } from '../../utils/formatter'
import * as Popover from '@radix-ui/react-popover'

// ID especial para o "setor" virtual de Templates
const TEMPLATES_SECTOR_ID = '__TEMPLATES__'

interface ProcessOption {
  value: string | number
  label: string
  setor_processo_fk: string | number
  template_schema?: unknown // Adicionar suporte a template_schema
  instrucoes?: string
  links?: Array<{ link: string } | string>
  anexos?: Array<{ anexo: string; nome_anexo: string } | string>
  descricao?: string
}

interface SectorOption {
  value: string | number
  label: string
}

interface UnifiedProcessSelectorProps {
  processes: ProcessOption[]
  sectors: SectorOption[]
  selectedProcesses: (string | number)[]
  onProcessesChange: (processes: (string | number)[]) => void
  selectedSectors: (string | number)[]
  onSectorsChange: (sectors: (string | number)[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  maxHeight?: string | number // Permitir customização da altura máxima
  expandable?: boolean // Permitir que o dropdown seja expandível
  singleSelection?: boolean // Permitir apenas um processo por vez
  onProcessSelect?: () => void // Callback quando um processo é selecionado (para auto-avançar)
}

// Componente de preview do processo
const ProcessInfoPopover: React.FC<{ processo: ProcessOption; sectorName?: string }> = ({
  processo,
  sectorName,
}) => {
  const [isOpen, setIsOpen] = useState(false) // Controla abertura do popover com info do processo (função oculta)
  const [showAllLinks, setShowAllLinks] = useState(false) //

  const hasAnyInfo =
    processo.descricao ||
    processo.instrucoes ||
    (processo.links && processo.links.length > 0) ||
    (processo.anexos && processo.anexos.length > 0)

  if (!hasAnyInfo) return null

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <button
          type='button'
          className={cn(
            'p-1.5 rounded-full transition-all duration-200 flex-shrink-0 group relative',
            'hover:bg-primary/15 hover:scale-110',
            isOpen && 'bg-primary/20 scale-110'
          )}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          {/* <HiInformationCircle className={cn(
            "w-4 h-4 transition-colors duration-200",
            isOpen ? "text-primary dark:text-blue-400" : "text-primary/70 dark:text-blue-400/70 group-hover:text-primary dark:group-hover:text-blue-400"
          )} />
          
          <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Ver detalhes
          </span> */}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side='right'
          align='start'
          sideOffset={8}
          className='z-[10000] w-96 max-h-[32rem] overflow-hidden bg-white dark:bg-neutral-800 border-2 border-primary/20 dark:border-primary/30 rounded-xl shadow-2xl animate-in fade-in-0 zoom-in-95 duration-200'
        >
          {/* Header do Popover */}
          <div className='bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 p-4 border-b border-primary/20 dark:border-primary/30'>
            <div className='flex items-start justify-between'>
              <div className='flex-1 min-w-0'>
                <h3 className='font-bold text-base text-gray-900 dark:text-gray-100 mb-1 line-clamp-2'>
                  {processo.label}
                </h3>
                {sectorName && (
                  <div className='flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-400'>
                    <FaRegBuilding className='w-3 h-3' />
                    <span>{sectorName}</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className='ml-2 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors'
              >
                <span className='text-gray-500 dark:text-gray-400 text-lg'>×</span>
              </button>
            </div>
          </div>

          {/* Conteúdo com scroll */}
          <div className='overflow-y-auto max-h-[24rem] p-4'>
            <div className='space-y-4'>
              {/* ID do Processo */}
              <div className='bg-gray-50 dark:bg-neutral-700/50 p-3 rounded-lg'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center space-x-2'>
                    <span className='text-xs font-medium text-gray-500 dark:text-gray-400'>
                      ID:
                    </span>
                    <code className='text-xs font-mono text-gray-900 dark:text-gray-100 bg-white dark:bg-neutral-800 px-2 py-1 rounded'>
                      {processo.value}
                    </code>
                  </div>
                  <button
                    onClick={() => copyToClipboard(String(processo.value))}
                    className='text-xs text-primary hover:text-primary/80 transition-colors'
                    title='Copiar ID'
                  >
                    📋 Copiar
                  </button>
                </div>
              </div>

              {/* Descrição */}
              {processo.descricao && (
                <div className='bg-blue-50/50 dark:bg-blue-950/30 p-3 rounded-lg border border-blue-100 dark:border-blue-900'>
                  <h4 className='text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center space-x-2'>
                    <span className='text-lg'>📋</span>
                    <span>Descrição</span>
                  </h4>
                  <div
                    className='text-sm text-gray-700 dark:text-gray-300 leading-relaxed rich-text-content'
                    dangerouslySetInnerHTML={{ __html: processRichTextForDisplay(processo.descricao || '') }}
                  />
                </div>
              )}

              {/* Instruções */}
              {processo.instrucoes && processo.instrucoes.trim() && (
                <div className='bg-amber-50/50 dark:bg-amber-950/30 p-3 rounded-lg border border-amber-100 dark:border-amber-900'>
                  <h4 className='text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center space-x-2'>
                    <span className='text-lg'>📖</span>
                    <span>Instruções</span>
                  </h4>
                  <div
                    className='text-sm text-gray-700 dark:text-gray-300 leading-relaxed rich-text-content'
                    dangerouslySetInnerHTML={{ __html: processRichTextForDisplay(processo.instrucoes || '') }}
                  />
                </div>
              )}

              {/* Links */}
              {Array.isArray(processo.links) && processo.links.length > 0 && (
                <div className='bg-green-50/50 dark:bg-green-950/30 p-3 rounded-lg border border-green-100 dark:border-green-900'>
                  <div className='flex items-center justify-between mb-2'>
                    <h4 className='text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2'>
                      <span className='text-lg'>🔗</span>
                      <span>Links Relacionados</span>
                      <span className='text-xs font-normal text-gray-500 dark:text-gray-400'>
                        ({processo.links.length})
                      </span>
                    </h4>
                    {processo.links.length > 3 && (
                      <button
                        onClick={() => setShowAllLinks(!showAllLinks)}
                        className='text-xs text-primary hover:text-primary/80 transition-colors font-medium'
                      >
                        {showAllLinks ? '↑ Mostrar menos' : '↓ Mostrar todos'}
                      </button>
                    )}
                  </div>
                  <div className='space-y-2'>
                    {(showAllLinks ? processo.links : processo.links.slice(0, 3)).map(
                      (linkItem, i) => {
                        const link = typeof linkItem === 'string' ? linkItem : linkItem.link
                        return (
                          <div key={i} className='flex items-center space-x-2 group'>
                            <a
                              href={link}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='flex-1 text-sm text-primary hover:text-primary/80 hover:underline truncate transition-colors'
                              onClick={e => e.stopPropagation()}
                              title={link}
                            >
                              🌐 {link}
                            </a>
                            <button
                              onClick={() => copyToClipboard(link)}
                              className='opacity-0 group-hover:opacity-100 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-all'
                              title='Copiar link'
                            >
                              📋
                            </button>
                          </div>
                        )
                      }
                    )}
                  </div>
                </div>
              )}

              {/* Anexos */}
              {Array.isArray(processo.anexos) && processo.anexos.length > 0 && (
                <div className='bg-purple-50/50 dark:bg-purple-950/30 p-3 rounded-lg border border-purple-100 dark:border-purple-900'>
                  <h4 className='text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center space-x-2'>
                    <span className='text-lg'>📎</span>
                    <span>Templates e Anexos</span>
                    <span className='text-xs font-normal text-gray-500 dark:text-gray-400'>
                      ({processo.anexos.length})
                    </span>
                  </h4>
                  <div className='space-y-2'>
                    {processo.anexos.map((anexoItem, i) => {
                      const anexo = typeof anexoItem === 'string' ? anexoItem : anexoItem.anexo
                      const nome =
                        typeof anexoItem === 'object' && 'nome_anexo' in anexoItem
                          ? anexoItem.nome_anexo
                          : `Anexo ${i + 1}`

                      return (
                        <div
                          key={i}
                          className='flex items-center justify-between p-2 bg-white dark:bg-neutral-800 rounded border border-purple-200 dark:border-purple-800 group hover:border-purple-300 dark:hover:border-purple-700 transition-colors'
                        >
                          <div className='flex items-center space-x-2 min-w-0 flex-1'>
                            <span className='text-base'>📄</span>
                            <span
                              className='text-sm text-gray-700 dark:text-gray-300 truncate'
                              title={nome}
                            >
                              {nome}
                            </span>
                          </div>
                          <a
                            href={anexo}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-xs text-primary hover:text-primary/80 transition-colors flex-shrink-0 ml-2'
                            onClick={e => e.stopPropagation()}
                          >
                            ⬇️ Baixar
                          </a>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Rodapé informativo */}
              <div className='pt-2 border-t border-gray-200 dark:border-neutral-700'>
                <p className='text-xs text-gray-500 dark:text-gray-400 text-center italic'>
                  💡 Clique nos links para abrir em nova aba
                </p>
              </div>
            </div>
          </div>

          <Popover.Arrow className='fill-white dark:fill-neutral-800' />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

export const UnifiedProcessSelector: React.FC<UnifiedProcessSelectorProps> = ({
  processes,
  sectors,
  selectedProcesses,
  onProcessesChange,
  selectedSectors,
  onSectorsChange,
  placeholder = 'Digite o nome do processo ou clique em um setor...',
  className,
  disabled = false,
  maxHeight,
  expandable = false,
  singleSelection = false, // ✅ NOVO: Default false para manter comportamento anterior
  onProcessSelect, // ✅ NOVO: Callback para notificar seleção
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [hoveredSector, setHoveredSector] = useState<string | number | null>(null)
  const [dropdownHeight, setDropdownHeight] = useState<string>('60vh')
  const [dropdownWidth, setDropdownWidth] = useState<string | undefined>(undefined)

  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Focar input quando componente montar
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [])


  useEffect(() => {
    if (!containerRef.current) return () => {}

    const updateMetrics = () => {
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return
      //Altura
      const viewportHeigth = window.innerHeight
      const spaceBelow = viewportHeigth - rect.bottom - 40 // 40px de margem 
      const spaceAbove = rect.top - 20

      let calculatedHeight = '60vh'

      if (expandable) {
        const maxAvailableHeight = Math.max(spaceBelow, spaceAbove * 0.8)
        const minHeight = 200
        const maxDesiredHeight = maxHeight
          ? typeof maxHeight === 'string'
            ? parseInt(maxHeight)
            :maxHeight
          :Math.min(800, maxAvailableHeight)
        calculatedHeight = `${Math.max(minHeight, Math.min(maxDesiredHeight, maxAvailableHeight))}px`
      } else if (maxHeight) {
        calculatedHeight = typeof maxHeight === 'string' ? maxHeight : `${maxHeight}px`
      }

      setDropdownHeight(calculatedHeight)

      //Largura (desktop)
      const viewportWidth = window.innerWidth
      if (viewportWidth < 768) {
        //mobile: deixar classe w-full cuidar disso
        setDropdownWidth(undefined)
        return
      }

      const centerX = rect.left + rect.width / 2
      const leftSpace = centerX - rect.left
      const rightSpace = viewportWidth - centerX
      const maxHalfWidth = Math.min(leftSpace, rightSpace) - 8
      const finalWidth = Math.min(maxHalfWidth * 2, 1200) // limitar a 1200px para não ficar gigante em telas muito largas
      setDropdownWidth(finalWidth > 0 ? `${finalWidth}px` : `${rect.width}px`)
    }

    updateMetrics()
    window.addEventListener('resize', updateMetrics)

    // ResizeObserver detecta quando o container muda de tamanho (ex: sidebar expandindo/recolhendo)
    const resizeObserver = new ResizeObserver(updateMetrics)
    resizeObserver.observe(containerRef.current)

    return () => {
      window.removeEventListener('resize', updateMetrics)
      resizeObserver.disconnect()
    }
  }, [maxHeight, expandable])

  // Calcular altura dinâmica baseada no espaço disponível
  useEffect(() => {
    if (containerRef.current) {
      const updateHeight = () => {
        const rect = containerRef.current?.getBoundingClientRect()
        if (rect) {
          const viewportHeight = window.innerHeight
          const spaceBelow = viewportHeight - rect.bottom - 40 // 40px de margem para não tocar o fundo
          const spaceAbove = rect.top - 20

          let calculatedHeight = '60vh' // Padrão

          if (expandable) {
            // Se expandível, usar mais espaço disponível
            const maxAvailableHeight = Math.max(spaceBelow, spaceAbove * 0.8)
            const minHeight = 200 // Altura mínima
            const maxDesiredHeight = maxHeight
              ? typeof maxHeight === 'string'
                ? parseInt(maxHeight)
                : maxHeight
              : Math.min(800, maxAvailableHeight)

            calculatedHeight = `${Math.max(minHeight, Math.min(maxDesiredHeight, maxAvailableHeight))}px`
          } else if (maxHeight) {
            calculatedHeight = typeof maxHeight === 'string' ? maxHeight : `${maxHeight}px`
          }

          setDropdownHeight(calculatedHeight)
        }
      }

      updateHeight()
      window.addEventListener('resize', updateHeight)

      return () => {
        window.removeEventListener('resize', updateHeight)
      }
    }

    // Return empty cleanup function if condition not met
    return () => {}
  }, [maxHeight, expandable])

  // Filtrar processos baseado na busca e setores selecionados
  const filteredProcesses = useMemo(() => {
    let filtered = processes

    // Filtrar por setores selecionados (SE houver algum selecionado)
    if (selectedSectors.length > 0) {
      filtered = filtered.filter(process => {
        // Se Templates estiver selecionado E o processo tem template
        const isTemplateMatch =
          selectedSectors.includes(TEMPLATES_SECTOR_ID) && process.template_schema

        // Se algum setor regular está selecionado E o processo pertence a ele
        const regularSectorsSelected = selectedSectors.filter(s => s !== TEMPLATES_SECTOR_ID)
        const isSectorMatch =
          regularSectorsSelected.length > 0 &&
          regularSectorsSelected.includes(process.setor_processo_fk)

        // Incluir se corresponder a Templates OU a um setor regular
        return isTemplateMatch || isSectorMatch
      })
    }
    // Se NENHUM setor está selecionado, mostrar TODOS os processos

    // Filtrar por busca
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()

      // Encontrar setores que correspondem à busca (incluindo Templates)
      const matchingSectors = sectors
        .filter(sector => sector.label.toLowerCase().includes(query))
        .map(sector => sector.value)

      // Incluir TEMPLATES_SECTOR_ID se a busca corresponder
      if ('templates'.includes(query) || 'formulários'.includes(query) || 'rq'.includes(query)) {
        matchingSectors.push(TEMPLATES_SECTOR_ID)
      }

      filtered = filtered.filter(process => {
        // Incluir se o nome do processo contém o termo de busca
        const processMatches = process.label.toLowerCase().includes(query)

        // Incluir se o processo pertence a um setor que corresponde à busca
        const sectorMatches = matchingSectors.includes(process.setor_processo_fk)

        // Incluir se é template e "Templates" corresponde à busca
        const templateMatches =
          process.template_schema && matchingSectors.includes(TEMPLATES_SECTOR_ID)

        return processMatches || sectorMatches || templateMatches
      })
    }

    return filtered
  }, [processes, selectedSectors, searchQuery, sectors])

  // Contar processos por setor (para os chips)
  const sectorCounts = useMemo(() => {
    const counts = new Map<string | number, number>()

    // Contar processos regulares por setor
    processes.forEach(process => {
      const currentCount = counts.get(process.setor_processo_fk) || 0
      counts.set(process.setor_processo_fk, currentCount + 1)
    })

    // Contar processos com template para o setor virtual Templates
    const templatesCount = processes.filter(p => p.template_schema).length
    if (templatesCount > 0) {
      counts.set(TEMPLATES_SECTOR_ID, templatesCount)
    }

    return counts
  }, [processes])

  // Obter setores com contadores (incluindo Templates)
  const sectorsWithCounts = useMemo(() => {
    const regularSectors = sectors.map(sector => ({
      ...sector,
      count: sectorCounts.get(sector.value) || 0,
    }))

    // Adicionar setor virtual de Templates no início
    const templatesCount = sectorCounts.get(TEMPLATES_SECTOR_ID) || 0
    if (templatesCount > 0) {
      const templatesSector = {
        value: TEMPLATES_SECTOR_ID,
        label: 'Templates Rápidos',
        count: templatesCount,
      }
      return [templatesSector, ...regularSectors.sort((a, b) => b.count - a.count)]
    }

    return regularSectors.sort((a, b) => b.count - a.count)
  }, [sectors, sectorCounts])

  // Filtrar setores baseado na busca (mostrar apenas setores que têm processos relevantes)
  const filteredSectors = useMemo(() => {
    if (!searchQuery.trim()) {
      return sectorsWithCounts
    }

    const query = searchQuery.toLowerCase()

    // Buscar setores que correspondem à busca diretamente
    const directSectorMatches = sectorsWithCounts.filter(sector =>
      sector.label.toLowerCase().includes(query)
    )

    // Buscar setores que têm processos correspondentes à busca
    const sectorsWithMatchingProcesses = sectorsWithCounts.filter(sector => {
      return processes.some(
        process =>
          process.setor_processo_fk === sector.value && process.label.toLowerCase().includes(query)
      )
    })

    // Combinar e remover duplicatas
    const combinedSectors = [...directSectorMatches]
    sectorsWithMatchingProcesses.forEach(sector => {
      if (!combinedSectors.find(s => s.value === sector.value)) {
        combinedSectors.push(sector)
      }
    })

    return combinedSectors.sort((a, b) => b.count - a.count)
  }, [sectorsWithCounts, searchQuery, processes])

  // Agrupar processos por setor
  const groupedProcesses = useMemo(() => {
    const groups = new Map<
      string | number,
      {
        sector: SectorOption
        processes: ProcessOption[]
      }
    >()

    filteredProcesses.forEach(process => {
      // Se Templates está selecionado E o processo tem template, adicionar ao grupo Templates
      if (selectedSectors.includes(TEMPLATES_SECTOR_ID) && process.template_schema) {
        if (!groups.has(TEMPLATES_SECTOR_ID)) {
          groups.set(TEMPLATES_SECTOR_ID, {
            sector: { value: TEMPLATES_SECTOR_ID, label: 'Templates Rápidos' },
            processes: [],
          })
        }
        groups.get(TEMPLATES_SECTOR_ID)!.processes.push(process)
      }

      // Agrupar por setor regular
      // Se NENHUM setor está selecionado OU se o setor do processo está selecionado
      const sector = sectors.find(s => s.value === process.setor_processo_fk)
      if (sector) {
        const shouldInclude =
          selectedSectors.length === 0 || selectedSectors.includes(process.setor_processo_fk)

        if (shouldInclude) {
          if (!groups.has(sector.value)) {
            groups.set(sector.value, {
              sector,
              processes: [],
            })
          }
          // Só adicionar se não for um processo com template que já foi adicionado ao grupo Templates
          const alreadyInTemplates =
            selectedSectors.includes(TEMPLATES_SECTOR_ID) && process.template_schema
          if (!alreadyInTemplates) {
            groups.get(sector.value)!.processes.push(process)
          }
        }
      }
    })

    // Ordenar: Templates primeiro, depois setores regulares alfabeticamente
    return Array.from(groups.values()).sort((a, b) => {
      if (a.sector.value === TEMPLATES_SECTOR_ID) return -1
      if (b.sector.value === TEMPLATES_SECTOR_ID) return 1
      return a.sector.label.localeCompare(b.sector.label)
    })
  }, [filteredProcesses, sectors, selectedSectors])

  const handleSectorToggle = (sectorValue: string | number) => {
    if (selectedSectors.includes(sectorValue)) {
      onSectorsChange(selectedSectors.filter(s => s !== sectorValue))
    } else {
      onSectorsChange([...selectedSectors, sectorValue])
    }
  }

  const handleProcessToggle = (processValue: string | number) => {
    if (singleSelection) {
      // Modo de seleção única: se clicar no mesmo, deseleciona, senão substitui
      if (selectedProcesses.includes(processValue)) {
        onProcessesChange([])
      } else {
        onProcessesChange([processValue])
        // Chamar callback após selecionar um processo
        if (onProcessSelect) {
          // Usar setTimeout para garantir que o estado seja atualizado primeiro
          setTimeout(() => onProcessSelect(), 100)
        }
      }
    } else {
      // Modo de múltipla seleção: toggle normal
      if (selectedProcesses.includes(processValue)) {
        onProcessesChange(selectedProcesses.filter(p => p !== processValue))
      } else {
        onProcessesChange([...selectedProcesses, processValue])
      }
    }
  }

  const handleClearSectors = () => {
    onSectorsChange([])
  }

  const getSectorIcon = (sectorName: string, sectorValue?: string | number) => {
    // Ícone especial para Templates
    if (sectorValue === TEMPLATES_SECTOR_ID) return '📋'

    const name = sectorName.toLowerCase()
    if (name.includes('rh') || name.includes('recursos')) return '🏢'
    if (name.includes('financeiro') || name.includes('contab')) return '💰'
    if (name.includes('ti') || name.includes('tecnologia')) return '🔧'
    if (name.includes('juridico') || name.includes('legal')) return '⚖️'
    if (name.includes('marketing') || name.includes('comunicacao')) return '📢'
    if (name.includes('gestao') || name.includes('administr')) return '🎯'
    if (name.includes('bi') || name.includes('inteligencia')) return '📊'
    return '📁'
  }

  // Obter processos mais comuns (simulado por agora)
  // const getProcessPopularity = (processLabel: string) => {
  //   const common = ['aprovação', 'solicitação', 'análise', 'processo']
  //   return common.some(word => processLabel.toLowerCase().includes(word))
  // }

  const selectedProcessesCount = selectedProcesses.length
  const totalFilteredProcesses = filteredProcesses.length

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {/* Container com padding para acomodar sombras */}
      <div className='p-1'>
        {/* Input Principal */}
        <div
          className={cn(
            'w-full min-h-[56px] md:h-[70px] px-3 md:px-6 py-3 md:py-4 border rounded-xl md:rounded-2xl transition-all duration-300',
            'bg-white/95 dark:bg-neutral-800/95 backdrop-blur-xl',
            'border-gray-200 dark:border-neutral-700 focus-within:border-primary/50',
            'shadow-lg shadow-black/5 dark:shadow-black/30',
            'border-primary/50 shadow-primary/10 shadow-xl',
            disabled && 'opacity-50 cursor-not-allowed',
            'flex items-center'
          )}
        >
          <div className='flex items-center justify-between w-full'>
            <div className='flex-1 flex items-center space-x-2 md:space-x-4'>
              <HiMagnifyingGlass className='w-5 h-5 md:w-6 md:h-6 text-muted-foreground dark:text-gray-300 flex-shrink-0' />
              <div className='flex-1 min-w-0'>
                <input
                  ref={searchInputRef}
                  type='text'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder={placeholder}
                  className='w-full bg-transparent outline-none text-base md:text-lg text-gray-900 dark:text-gray-100 placeholder:text-muted-foreground dark:placeholder:text-gray-400'
                  disabled={disabled}
                />
              </div>
            </div>
            <div className='flex items-center space-x-2 md:space-x-3 flex-shrink-0'>
              {selectedProcessesCount > 0 && (
                <div className='text-xs md:text-sm text-primary dark:text-blue-300 font-medium whitespace-nowrap'>
                  {selectedProcessesCount}{' '}
                  {selectedProcessesCount > 1 ? (
                    <span className='hidden sm:inline'>selecionados</span>
                  ) : (
                    <span className='hidden sm:inline'>selecionado</span>
                  )}
                  <span className='sm:hidden'>sel.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown - Sempre visível */}
      <div
        ref={dropdownRef}
        className={cn(
          'absolute top-full mt-2 bg-white/98 dark:bg-neutral-900/98 border border-gray-200/80 dark:border-neutral-700/80 rounded-xl md:rounded-2xl',
          'overflow-hidden flex flex-col backdrop-blur-xl',
          'shadow-xl shadow-black/10 dark:shadow-black/50',
          // Garantir que o dropdown esteja sempre visível
          'z-[9999]',
          // Mobile: largura quase total
          'w-full',
          // Desktop: largura relativa ao container
          'md:w-[84vw] md:max-w-[1200px]',
          // Centralizar em desktop
          'md:left-1/2 md:-translate-x-1/2'
        )}
        style={{
          maxHeight: dropdownHeight,
          minHeight: '400px',
          ...(expandable && { minHeight: '450px' }),
          ...(dropdownWidth ? { width: dropdownWidth} : {})
        }}
      >
          {/* Layout: empilhado em mobile, lado a lado em desktop */}
          <div className='flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden'>
            {/* Coluna dos Setores */}
            <div
              className={cn(
                'border-b md:border-b-0 md:border-r border-gray-200/60 dark:border-neutral-700/60 flex flex-col',
                'bg-gray-50/80 dark:bg-neutral-800/80',
                // Mobile: altura fixa menor para dar mais espaço aos processos
                'max-h-[25vh] md:max-h-none',
                // Desktop: largura fixa
                'w-full md:w-1/3'
              )}
            >
              <div className='p-3 md:p-4 border-b border-border/50 flex-shrink-0'>
                <div className='flex items-center justify-between'>
                  <h3 className='text-xs md:text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center space-x-1 md:space-x-2'>
                    <FaRegBuilding className='w-3 h-3 md:w-4 md:h-4 text-primary' />
                    <span>SETORES</span>
                    {searchQuery.trim() && (
                      <span className='text-xs md:text-sm text-muted-foreground dark:text-gray-300 font-normal'>
                        ({filteredSectors.length})
                      </span>
                    )}
                  </h3>
                  {selectedSectors.length > 0 && (
                    <button
                      onClick={handleClearSectors}
                      className='text-sm text-muted-foreground dark:text-gray-300 hover:text-foreground dark:hover:text-gray-100 transition-colors'
                    >
                      Limpar
                    </button>
                  )}
                </div>
              </div>

              <div className='flex-1 overflow-y-auto'>
                <div className='p-2 md:p-4'>
                  {searchQuery.trim() && filteredSectors.length === 0 ? (
                    <div className='text-center py-4 md:py-8 text-muted-foreground dark:text-gray-300'>
                      <div className='text-2xl md:text-3xl mb-2'>🔍</div>
                      <p className='text-xs md:text-sm'>Nenhum setor encontrado</p>
                    </div>
                  ) : (
                    <div className='grid grid-cols-2 md:grid-cols-1 gap-2 md:gap-3'>
                      {filteredSectors.map(sector => (
                        <button
                          key={sector.value}
                          onClick={() => handleSectorToggle(sector.value)}
                          onMouseEnter={() => setHoveredSector(sector.value)}
                          onMouseLeave={() => setHoveredSector(null)}
                          className={cn(
                            'w-full flex items-center justify-between p-2 md:p-3 rounded-lg text-left transition-all duration-200',
                            'border border-gray-200/70 dark:border-neutral-600/70 hover:border-primary/50',
                            selectedSectors.includes(sector.value)
                              ? 'bg-primary dark:bg-primary text-primary-foreground border-primary shadow-sm'
                              : 'bg-white/90 dark:bg-neutral-700/90 hover:bg-primary/5 dark:hover:bg-primary/10',
                            hoveredSector === sector.value && 'md:scale-[1.02] shadow-md',
                            // Destaque especial para Templates
                            sector.value === TEMPLATES_SECTOR_ID &&
                              !selectedSectors.includes(sector.value) &&
                              'dark:from-emerald-900/20 dark:to-teal-900/20'
                          )}
                        >
                          <div className='flex items-center space-x-1 md:space-x-2 min-w-0 flex-1'>
                            <span className='text-sm md:text-base'>
                              {getSectorIcon(sector.label, sector.value)}
                            </span>
                            <span
                              className={cn(
                                'text-xs md:text-sm font-medium truncate',
                                selectedSectors.includes(sector.value)
                                  ? 'text-primary-foreground'
                                  : sector.value === TEMPLATES_SECTOR_ID
                                    ? 'dark:text-emerald-200'
                                    : 'text-gray-800 dark:text-gray-100'
                              )}
                            >
                              {sector.label}
                            </span>
                          </div>
                          <span
                            className={cn(
                              'text-xs md:text-sm px-1.5 md:px-2 py-0.5 rounded-full flex-shrink-0 ml-1 md:ml-2',
                              selectedSectors.includes(sector.value)
                                ? 'bg-primary-foreground/20 text-primary-foreground'
                                : sector.value === TEMPLATES_SECTOR_ID
                                  ? 'dark:bg-emerald-500/40 dark:text-emerald-200'
                                  : 'bg-primary/30 text-primary dark:bg-primary/40 dark:text-blue-300'
                            )}
                          >
                            {sector.count}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Coluna dos Processos */}
            <div className='flex-1 flex flex-col min-w-0 min-h-0'>
              <div className='p-3 md:p-4 border-b border-border/50 flex-shrink-0'>
                <div className='flex items-center justify-between'>
                  <h3 className='text-xs md:text-sm font-semibold text-gray-800 dark:text-gray-100 flex items-center space-x-1 md:space-x-2'>
                    <span>📋</span>
                    <span>PROCESSOS</span>
                  </h3>
                  <div className='text-xs md:text-sm text-muted-foreground dark:text-gray-300'>
                    {totalFilteredProcesses}{' '}
                    <span className='hidden sm:inline'>
                      encontrado{totalFilteredProcesses !== 1 ? 's' : ''}
                    </span>
                    {searchQuery.trim() && (
                      <span className='ml-1 text-primary font-medium hidden md:inline'>
                        • busca ativa
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className='flex-1 overflow-y-auto min-h-0'>
                <div className='p-2 md:p-4'>
                  {groupedProcesses.length === 0 ? (
                    <div className='text-center py-6 md:py-8 text-muted-foreground dark:text-gray-300'>
                      <div className='text-3xl md:text-4xl mb-2'>🔍</div>
                      <p className='text-xs md:text-sm px-4'>
                        {searchQuery
                          ? `Nenhum processo encontrado para "${searchQuery}"`
                          : selectedSectors.length > 0
                            ? 'Nenhum processo encontrado nos setores selecionados'
                            : 'Selecione setores à esquerda para ver os processos'}
                      </p>
                    </div>
                  ) : (
                    <div className='space-y-3 md:space-y-4'>
                      {groupedProcesses.map(({ sector, processes: sectorProcesses }) => (
                        <div key={sector.value}>
                          {/* títulos */}
                          {/* <div
                            className={cn(
                              'flex items-center space-x-1 md:space-x-2 mb-2 md:mb-3 pb-2 border-b border-border/30 dark:border-neutral-700/40',
                              sector.value === TEMPLATES_SECTOR_ID &&
                                'border-emerald-300/40 dark:border-emerald-600/40'
                            )}
                          >
                            <span className='text-sm md:text-base'>
                              {getSectorIcon(sector.label, sector.value)}
                            </span>
                            <h4
                              className={cn(
                                'font-semibold text-sm md:text-base truncate',
                                sector.value === TEMPLATES_SECTOR_ID
                                  ? 'text-emerald-800 dark:text-emerald-200'
                                  : 'text-gray-800 dark:text-gray-100'
                              )}
                            >
                              {sector.label}
                            </h4>
                            <span
                              className={cn(
                                'text-xs md:text-sm px-1.5 md:px-2 py-0.5 md:py-1 rounded-full flex-shrink-0',
                                sector.value === TEMPLATES_SECTOR_ID
                                  ? 'text-emerald-800 dark:text-emerald-200 bg-emerald-100 dark:bg-emerald-800/50'
                                  : 'text-muted-foreground dark:text-gray-300 bg-gray-100 dark:bg-neutral-700'
                              )}
                            >
                              {sectorProcesses.length}
                            </span>
                          </div> */}

                          <div className='grid grid-cols-1 gap-2 md:gap-3'>
                            {sectorProcesses.map(process => {
                              const isSelected = selectedProcesses.includes(process.value)
                              //const isPopular = getProcessPopularity(process.label)

                              // Verificar se o processo foi encontrado por correspondência de setor
                              const query = searchQuery.toLowerCase()
                              const processMatchesDirectly = process.label
                                .toLowerCase()
                                .includes(query)
                              const sectorMatchesQuery = sector.label.toLowerCase().includes(query)
                              const foundBySector =
                                searchQuery.trim() && !processMatchesDirectly && sectorMatchesQuery

                              return (
                                <label
                                  key={process.value}
                                  onClick={e => {
                                    // Prevenir duplo clique se clicar no popover ou checkbox
                                    const target = e.target as HTMLElement
                                    if (
                                      target.closest('button[type="button"]') ||
                                      target.closest('input[type="checkbox"]')
                                    ) {
                                      return
                                    }
                                    handleProcessToggle(process.value)
                                  }}
                                  className={cn(
                                    'flex items-center p-3 md:p-4 rounded-lg cursor-pointer transition-all duration-200',
                                    'border border-gray-200/60 dark:border-neutral-600/60 hover:border-primary/30 hover:shadow-sm',
                                    'bg-white/90 dark:bg-neutral-800/90',
                                    isSelected &&
                                      'bg-primary/10 dark:bg-primary/20 border-primary/50 dark:border-primary/60 shadow-sm',
                                    foundBySector &&
                                      'bg-blue-50/90 dark:bg-blue-950/50 border-blue-200/60 dark:border-blue-800/60',
                                    'space-x-2 md:space-x-4'
                                  )}
                                >
                                  {!singleSelection && (
                                    <input
                                      type='checkbox'
                                      checked={isSelected}
                                      onChange={() => handleProcessToggle(process.value)}
                                      className='w-4 h-4 md:w-5 md:h-5 text-primary border-border rounded focus:ring-primary/20 focus:ring-2 flex-shrink-0'
                                    />
                                  )}
                                  {singleSelection && isSelected && (
                                    <div className='w-4 h-4 md:w-5 md:h-5 rounded-full bg-primary dark:bg-primary flex items-center justify-center flex-shrink-0'>
                                      <span className='text-white text-xs'>✓</span>
                                    </div>
                                  )}
                                  <div className='flex-1 min-w-0'>
                                    <div className='flex items-center justify-between gap-2'>
                                      <span
                                        className={cn(
                                          'text-sm md:text-base font-medium truncate',
                                          'text-gray-900 dark:text-gray-100'
                                        )}
                                      >
                                        {process.label}
                                      </span>
                                      <div className='flex items-center space-x-1 md:space-x-2 flex-shrink-0'>
                                        {/* Info Popover */}
                                        <ProcessInfoPopover
                                          processo={process}
                                          sectorName={sector.label}
                                        />

                                        {foundBySector && (
                                          <span className='text-xs text-blue-700 dark:text-blue-200 bg-blue-100 dark:bg-blue-800 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full hidden sm:inline'>
                                            Por setor
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </label>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer com estatísticas */}
          {selectedProcessesCount > 0 && (
            <div className='border-t border-gray-200/60 dark:border-neutral-700/60 p-2 md:p-3 bg-gray-50/80 dark:bg-neutral-800/80 flex-shrink-0'>
              <div className='flex items-center justify-between text-xs md:text-sm'>
                <span className='text-muted-foreground dark:text-gray-400'>
                  <span className='hidden sm:inline'>Processos selecionados:</span>
                  <span className='sm:hidden'>Selecionados:</span>
                </span>
                <span className='font-semibold text-primary'>{selectedProcessesCount}</span>
              </div>
            </div>
          )}
        </div>
      </div>
  )
}
